// ENG-4219 / ENG-4543 — bounded start-signal retry policy.
//
// The previous <Signal> implementation ran an UNBOUNDED `while (!done)` loop:
// sendSignal() throws on a NATS timeout, the catch only logged, slept 1s and
// retried forever. A bridge-less "limbo" studio therefore spun on
// "Loading your studio…" indefinitely — the loop had no terminal state.
// This module owns the decision logic as a pure, dependency-injected function
// so the failure modes are unit-testable without React, timers-mocking, or
// ESM module mocks (sibling precedent: watchdog-honesty.ts).
//
// Contract: EVERY branch settles to a definite state — 'booting' (signal
// accepted), 'unreachable' (attempt budget exhausted → caller flips the agent
// to a user-visible offline/unreachable state), or 'cancelled' (component
// unmounted / effect re-ran; the next effect run owns the agent).
//
// Per rule: code-comments-required + reference-rules-in-comments
// (ai.knowledge/global). Per rule: think-in-systems — the policy is one
// composable unit, the React wiring stays in signal.tsx.

/**
 * Attempt budget. 5 attempts with the backoff below is ~15s of sleeps plus
 * the per-request NATS timeout each attempt already carries — comfortably
 * covers the EXPECTED boot race (agent not yet subscribed when the first
 * signal fires; 82% of the ENG-4543 occurrences succeed on retry) without
 * hammering a genuinely dead bridge forever.
 */
export const START_SIGNAL_MAX_ATTEMPTS = 5;

/**
 * Exponential backoff for the sleep AFTER failed attempt `attempt` (1-based):
 * 1s, 2s, 4s, 8s — capped so a mid-range budget bump never produces a
 * multi-minute silent gap.
 */
export const START_SIGNAL_BACKOFF_CAP_MS = 8_000;

export const startSignalRetryDelayMs = (attempt: number): number =>
  Math.min(1_000 * 2 ** Math.max(attempt - 1, 0), START_SIGNAL_BACKOFF_CAP_MS);

/** Definite settle states of one start-signal loop run. */
export type StartSignalOutcome = 'booting' | 'cancelled' | 'unreachable';

export type StartSignalLoopDeps = {
  /** One signal attempt; rejects on failure (NATS timeout, bad reply). */
  readonly sendSignal: () => Promise<void>;

  /** Settle: the agent accepted the signal — mark it booting in the depot. */
  readonly onBooting: () => void;

  /**
   * Settle: attempt budget exhausted — the bridge is unreachable. The caller
   * captures EXACTLY ONE error here (ENG-4543: transient attempts must NOT
   * each become a captured exception) and flips the agent to a terminal,
   * user-visible offline state (ENG-4219).
   */
  readonly onUnreachable: (error: unknown) => void;

  /**
   * An EXPECTED transient failure (attempt < budget). Log at warning/info —
   * never as a captured exception (ENG-4543: 82% of the 2,892 PostHog
   * occurrences were this boot race, polluting affected-user counts).
   */
  readonly onTransientFailure: (error: unknown, attempt: number) => void;

  /** Backoff sleep between attempts. */
  readonly sleep: (ms: number) => Promise<void>;

  /** True once the owning effect is torn down — stop without settling. */
  readonly isCancelled: () => boolean;
};

/**
 * Run the bounded start-signal loop. Never throws: sendSignal rejections are
 * routed to onTransientFailure/onUnreachable, so a fire-and-forget caller can
 * not leak an unhandled rejection into PostHog exception autocapture.
 */
export const runStartSignalLoop = async (
  deps: StartSignalLoopDeps,
  maxAttempts: number = START_SIGNAL_MAX_ATTEMPTS
): Promise<StartSignalOutcome> => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (deps.isCancelled()) {
      return 'cancelled';
    }

    try {
      // eslint-disable-next-line no-await-in-loop
      await deps.sendSignal();

      deps.onBooting();

      return 'booting';
    } catch (error) {
      if (attempt >= maxAttempts) {
        // Terminal: exactly one captured error for the whole run (ENG-4543),
        // then the caller settles the agent to a definite state (ENG-4219).
        deps.onUnreachable(error);

        return 'unreachable';
      }

      deps.onTransientFailure(error, attempt);
    }

    if (deps.isCancelled()) {
      return 'cancelled';
    }

    // eslint-disable-next-line no-await-in-loop
    await deps.sleep(startSignalRetryDelayMs(attempt));
  }

  // Unreachable by construction (the attempt === maxAttempts iteration always
  // returns), but every branch must settle to a definite state (ENG-4219).
  return 'cancelled';
};
